<!-- ── Dialog wrapper ─────────────────────────────────────────────────────── -->
<div class="import-dialog">
  <!-- ── Header ────────────────────────────────────────────────────────────── -->
  <div class="import-dialog__header">
    <h2 class="import-dialog__title">{{ titleKey | translate }}</h2>
    <button class="import-dialog__close" type="button" (click)="close()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- ── Step indicator ────────────────────────────────────────────────────── -->
  <div class="import-dialog__steps">
    <span
      class="import-dialog__step"
      [class.import-dialog__step--active]="currentStep === 'upload'"
      [class.import-dialog__step--done]="currentStep !== 'upload'"
    >
      <span class="import-dialog__step-circle">
        <mat-icon *ngIf="currentStep !== 'upload'" class="import-dialog__step-check">check</mat-icon>
      </span>
      {{ "knowledge_base.import.step_upload" | translate }}
    </span>
    <!-- The mapping step appears only for a file that needs one: a template
         file goes straight through, which is what the template is FOR. -->
    <ng-container *ngIf="canMap">
      <span class="import-dialog__step-arrow">›</span>
      <span
        class="import-dialog__step"
        [class.import-dialog__step--active]="currentStep === 'mapping'"
        [class.import-dialog__step--done]="currentStep === 'preview'"
      >
        <span class="import-dialog__step-circle">
          <mat-icon *ngIf="currentStep === 'preview'" class="import-dialog__step-check">check</mat-icon>
        </span>
        {{ "knowledge_base.import.step_mapping" | translate }}
      </span>
    </ng-container>
    <span class="import-dialog__step-arrow">›</span>
    <span class="import-dialog__step" [class.import-dialog__step--active]="currentStep === 'preview'">
      <span class="import-dialog__step-circle"></span>
      {{ "knowledge_base.import.step_preview" | translate }}
    </span>
  </div>

  <!-- ── Info line ──────────────────────────────────────────────────────────── -->
  <div class="import-dialog__info-row">
    <mat-icon class="import-dialog__info-icon">info_outline</mat-icon>
    <p class="import-dialog__info">{{ infoKey | translate }}</p>
  </div>

  <!-- ══ UPLOAD STEP ══════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="currentStep === 'upload'">
    <!-- Template download buttons. Hidden for price sources: an email export
         is written by Outlook, so offering a template would be noise. -->
    <div class="import-dialog__template-row" *ngIf="showsTemplate">
      <span class="import-dialog__template-label">{{ "knowledge_base.import.download_template" | translate }}:</span>
      <button class="import-dialog__template-btn" type="button" (click)="downloadTemplate('csv')">
        <mat-icon class="import-dialog__template-icon">file_download</mat-icon>
        .csv
      </button>
      <button class="import-dialog__template-btn" type="button" (click)="downloadTemplate('xlsx')">
        <mat-icon class="import-dialog__template-icon">file_download</mat-icon>
        .xlsx
      </button>
    </div>

    <!-- Drop zone -->
    <div
      class="import-dialog__dropzone"
      [class.import-dialog__dropzone--dragover]="isDragOver"
      [class.import-dialog__dropzone--has-file]="!!selectedFile"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave($event)"
      (drop)="onDrop($event)"
      (click)="fileInput.click()"
    >
      <input
        #fileInput
        type="file"
        [accept]="acceptAttr"
        [multiple]="allowsMultipleFiles"
        class="import-dialog__file-input"
        (change)="onFileInputChange($event)"
      />

      <ng-container *ngIf="!selectedFile">
        <mat-icon class="import-dialog__dropzone-icon">cloud_upload</mat-icon>
        <p class="import-dialog__dropzone-text">{{ "knowledge_base.import.drag_drop" | translate }}</p>
        <p class="import-dialog__dropzone-hint">
          {{ (isPriceObservationType ? "knowledge_base.import.drag_drop_hint_prices" : "knowledge_base.import.drag_drop_hint") | translate:{ extensions: acceptedExtensionsLabel } }}
        </p>
      </ng-container>

      <ng-container *ngIf="hasSelection">
        <mat-icon class="import-dialog__dropzone-icon import-dialog__dropzone-icon--success">check_circle</mat-icon>
        <p class="import-dialog__dropzone-text">
          {{ "knowledge_base.import.file_selected" | translate }}: {{ allowsMultipleFiles ? fileNames : selectedFile?.name }}
        </p>
      </ng-container>
    </div>

    <!-- File error -->
    <p *ngIf="fileError" class="import-dialog__file-error">{{ fileError }}</p>

    <!-- A spreadsheet that does not follow the template is not refused: it is
         MAPPED. CONTINUE opens the mapping screen, and the template stays a
         click away for whoever would rather reshape the file. A file with no
         readable table at all (`canMap` false) has no question to ask, and
         the refusal stands as it is. -->
    <div
      *ngIf="templateRefusal"
      class="import-dialog__refusal"
    >
      <mat-icon class="import-dialog__refusal-icon">{{ canMap ? "tune" : "block" }}</mat-icon>
      <div class="import-dialog__refusal-text">
        <strong>{{ "knowledge_base.import.price_observations.not_conform" | translate }}</strong>
        <span *ngIf="canMap">
          {{ "knowledge_base.import.price_observations.map_instead" | translate }}
        </span>
        <span>
          {{ "knowledge_base.import.price_observations.expected_columns" | translate }}
          {{ (templateRefusal.expected_columns || []).join(", ") }}
        </span>
        <span *ngIf="templateRefusal.missing_columns?.length">
          {{ "knowledge_base.import.price_observations.missing_columns" | translate }}
          {{ (templateRefusal.missing_columns || []).join(", ") }}
        </span>
        <button class="import-dialog__template-btn" type="button" (click)="downloadTemplate('csv')">
          <mat-icon class="import-dialog__template-icon">file_download</mat-icon>
          {{ "knowledge_base.import.download_template" | translate }}
        </button>
      </div>
    </div>

    <!-- "These prices apply to" lives in the drop step, asked with the KB's
         own audience picker rather than with buttons of this dialog's
         invention. Four answers: the file states it (its `customer` column,
         resolved row by row) — the default — or the batch is for everyone,
         for one customer, or for named groups.
         It shows on a REFUSED file too. It used to be hidden as soon as the
         file did not follow the template — which is exactly the file that
         goes to the mapping screen, where "this file names no customer:
         choose an audience at the previous step" then pointed at a question
         that was not on the previous step. Any non-template sheet without a
         customer column could not be imported at all. -->
    <div class="import-dialog__scope" *ngIf="needsScope">
      <app-audience-scope-picker
        [options]="availableAudiences"
        [labelKeys]="audienceLabelKeys"
        [value]="audienceScope"
        [hintKey]="scopeHintKey"
        labelKey="knowledge_base.import.scope.title"
        (valueChange)="onAudienceScopeChange($event)"
      ></app-audience-scope-picker>
    </div>
  </ng-container>
  <!-- ══ END UPLOAD STEP ══════════════════════════════════════════════════════ -->

  <!-- ══ MAPPING STEP ═════════════════════════════════════════════════════════ -->
  <!-- Only for a spreadsheet the template does not describe. Everything it
       renders came back with the drop step's own `inspect` answer. -->
  <app-price-column-mapping-step
    *ngIf="currentStep === 'mapping'"
    [inspection]="templateRefusal"
    [scope]="mappingScope"
    (confirmed)="onMappingConfirmed($event)"
    (cancelled)="back()"
  ></app-price-column-mapping-step>

  <!-- ══ PREVIEW STEP ═════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="currentStep === 'preview' && previewData && !isPriceObservationType">
    <!-- Overwrite warning (preferred products: existing pairs are replaced) -->
    <div *ngIf="isPreferredProductType" class="import-dialog__overwrite-warning">
      <mat-icon class="import-dialog__overwrite-warning-icon">warning_amber</mat-icon>
      <span>{{ "knowledge_base.import.preferred_products.overwrite_warning" | translate }}</span>
    </div>

    <!-- Summary cards -->
    <div class="import-dialog__summary">
      <div class="import-dialog__summary-card">
        <span class="import-dialog__summary-label">{{ "knowledge_base.import.summary.total_rows" | translate }}</span>
        <span class="import-dialog__summary-value">{{ totalRows }}</span>
      </div>
      <div class="import-dialog__summary-card import-dialog__summary-card--ready" *ngIf="validCount > 0">
        <span class="import-dialog__summary-label">{{ "knowledge_base.import.summary.valid" | translate }}</span>
        <span class="import-dialog__summary-value">{{ validCount }}</span>
      </div>
      <div class="import-dialog__summary-card import-dialog__summary-card--warning" *ngIf="warningCount > 0">
        <span class="import-dialog__summary-label">{{ "knowledge_base.import.summary.warnings" | translate }}</span>
        <span class="import-dialog__summary-subtitle">{{ "knowledge_base.import.summary.warnings_subtitle" | translate }}</span>
        <span class="import-dialog__summary-value">{{ warningCount }}</span>
      </div>
      <div class="import-dialog__summary-card import-dialog__summary-card--duplicates" *ngIf="duplicates > 0">
        <span class="import-dialog__summary-label">{{ "knowledge_base.import.summary.duplicates" | translate }}</span>
        <span class="import-dialog__summary-subtitle">{{ "knowledge_base.import.summary.duplicates_subtitle" | translate }}</span>
        <span class="import-dialog__summary-value">{{ duplicates }}</span>
      </div>
      <div class="import-dialog__summary-card import-dialog__summary-card--error" *ngIf="errors > 0">
        <span class="import-dialog__summary-label">{{ "knowledge_base.import.summary.errors" | translate }}</span>
        <span class="import-dialog__summary-value">{{ errors }}</span>
      </div>
    </div>

    <!-- Row table -->
    <div class="import-dialog__table-wrapper">
      <table class="import-dialog__table">
        <thead>
          <tr>
            <th *ngIf="!isPreferredProductType">
              {{ "knowledge_base.import.col.sku" | translate }}
              <mat-icon class="import-dialog__sort-icon">unfold_more</mat-icon>
            </th>
            <th *ngIf="isAliasType">
              {{ "knowledge_base.import.col.alias" | translate }}
              <mat-icon class="import-dialog__sort-icon">unfold_more</mat-icon>
            </th>
            <th *ngIf="!isAliasType">
              {{ "knowledge_base.import.col.customer" | translate }}
              <mat-icon class="import-dialog__sort-icon">unfold_more</mat-icon>
            </th>
            <th *ngIf="!isAliasType && !isPreferredProductType">
              {{ "knowledge_base.import.col.external_reference" | translate }}
              <mat-icon class="import-dialog__sort-icon">unfold_more</mat-icon>
            </th>
            <th *ngIf="isPreferredProductType">
              {{ "knowledge_base.import.col.product" | translate }}
              <mat-icon class="import-dialog__sort-icon">unfold_more</mat-icon>
            </th>
            <th>
              {{ "knowledge_base.import.col.status" | translate }}
              <mat-icon class="import-dialog__sort-icon">unfold_more</mat-icon>
            </th>
            <th>
              {{ "knowledge_base.import.col.reason" | translate }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of previewData.rows">
            <td *ngIf="!isPreferredProductType">{{ getDisplaySku(row) }}</td>
            <td *ngIf="isAliasType">{{ getDisplayAlias(row) }}</td>
            <td *ngIf="!isAliasType">{{ getDisplayCustomer(row) }}</td>
            <td *ngIf="!isAliasType && !isPreferredProductType">{{ getDisplayReferenceCode(row) }}</td>
            <td *ngIf="isPreferredProductType">{{ getDisplayProduct(row) }}</td>
            <td>
              <span class="import-dialog__badge import-dialog__badge--{{ getBadgeType(row) }}">
                {{ getBadgeLabel(row) }}
              </span>
            </td>
            <td class="import-dialog__reason-cell">{{ getRowReason(row) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </ng-container>
  <!-- ══ END PREVIEW STEP ══════════════════════════════════════════════════════ -->

  <!-- ══ PRICE-SOURCE PREVIEW ═════════════════════════════════════════════════ -->
  <ng-container *ngIf="currentStep === 'preview' && isPriceObservationType && pricePreview">
    <!-- The COUNTS are exact — the whole file was analysed; only the table
         below is a glimpse. The lines without a customer will land "Unknown
         customer, to correct", and the preview says so. -->
    <div class="import-dialog__overwrite-warning" *ngIf="unknownCustomerCount > 0">
      <mat-icon class="import-dialog__overwrite-warning-icon">info_outline</mat-icon>
      <span>{{ "knowledge_base.import.price_observations.unknown_customer_notice" | translate: { count: unknownCustomerCount } }}</span>
    </div>

    <!-- No scope question for an email export: a batch of .msg is
         multi-customer by nature, and a batch scope would overwrite the real
         attribution instead of simplifying it. -->
    <div *ngIf="scopeIsForbidden" class="import-dialog__info-row">
      <mat-icon class="import-dialog__info-icon">alternate_email</mat-icon>
      <p class="import-dialog__info">{{ "knowledge_base.import.price_observations.scope_forbidden" | translate }}</p>
    </div>

    <p *ngIf="previewError" class="import-dialog__file-error">{{ previewError }}</p>

    <!-- Read, and empty. CONFIRM is off: accepting it would grow a journal row
         for a file that produced nothing. -->
    <p *ngIf="emptyPreview" class="import-dialog__file-error">
      {{ "knowledge_base.import.price_observations.empty_preview" | translate }}
    </p>

    <!-- Summary cards -->
    <div class="import-dialog__summary">
      <div class="import-dialog__summary-card">
        <span class="import-dialog__summary-label">{{ "knowledge_base.import.summary.lines_extracted" | translate }}</span>
        <span class="import-dialog__summary-value">{{ linesExtracted }}</span>
      </div>
      <div class="import-dialog__summary-card import-dialog__summary-card--ready">
        <span class="import-dialog__summary-label">{{ "knowledge_base.import.summary.resolved_customer" | translate }}</span>
        <span class="import-dialog__summary-value">{{ resolvedCustomerCount }}</span>
      </div>
      <div class="import-dialog__summary-card import-dialog__summary-card--warning">
        <span class="import-dialog__summary-label">{{ "knowledge_base.import.summary.resolved_product" | translate }}</span>
        <span class="import-dialog__summary-value">{{ resolvedProductCount }}</span>
      </div>
    </div>

    <!-- Sample table -->
    <div class="import-dialog__table-wrapper">
      <table class="import-dialog__table">
        <thead>
          <tr>
            <th>{{ "knowledge_base.import.col.customer" | translate }}</th>
            <th>{{ "knowledge_base.import.col.product" | translate }}</th>
            <th>{{ "knowledge_base.import.col.price" | translate }}</th>
            <th>{{ "knowledge_base.import.col.source_file" | translate }}</th>
            <th>{{ "knowledge_base.import.col.status" | translate }}</th>
            <th>{{ "knowledge_base.import.col.reason" | translate }}</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of sampleRows">
            <td>{{ getSampleCustomer(row) }}</td>
            <td>{{ getSampleProduct(row) }}</td>
            <td>{{ getSamplePrice(row) }}</td>
            <td>{{ row.source_filename || "—" }}</td>
            <td>
              <span *ngIf="getSampleBadge(row) as badge"
                    class="import-dialog__badge import-dialog__badge--{{ badge.type }}">
                {{ badge.label }}
              </span>
            </td>
            <td class="import-dialog__reason-cell">{{ getSampleReason(row) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </ng-container>
  <!-- ══ END PRICE-SOURCE PREVIEW ═════════════════════════════════════════════ -->

  <!-- ── Loading overlay (dry-run in progress) ──────────────────────────────── -->
  <div *ngIf="isLoadingPreview" class="import-dialog__loading">
    <mat-spinner diameter="32"></mat-spinner>
  </div>

  <!-- ── Footer actions ────────────────────────────────────────────────────── -->
  <div class="import-dialog__footer">
    <!-- No Cancel: the header cross closes the wizard, and a second way out
         beside BACK only crowded the row that carries the step's action. -->

    <!-- Back, from the preview. The mapping step carries its own. -->
    <button class="import-dialog__cancel-btn"
            type="button"
            *ngIf="currentStep === 'preview'"
            (click)="back()">
      {{ "knowledge_base.import.back" | translate }}
    </button>

    <!-- Drop step: CONTINUE. Off until the audience is answered on a
         spreadsheet, and off on a refusal no mapping could answer. On a
         mappable one it opens the mapping screen rather than staying grey. -->
    <ui-button
      *ngIf="currentStep === 'upload'"
      type="primary"
      [label]="(templateRefusal && canMap ? 'knowledge_base.import.map_columns' : 'knowledge_base.import.proceed') | translate"
      [disabled]="!hasSelection || isLoadingPreview || isInspecting || !scopeAnswered || (!!templateRefusal && !canMap)"
      (click)="next()"
    >
    </ui-button>

    <!-- Preview: CONFIRM — never active over a preview in error. -->
    <ui-button
      *ngIf="currentStep === 'preview'"
      type="primary"
      [label]="'knowledge_base.import.confirm' | translate"
      [disabled]="!canConfirm"
      (click)="confirmImport()"
    >
    </ui-button>
  </div>
</div>
